/**
 * Passkey API Service — user portal.
 *
 * Wraps the six /v1_0/UserPortal/Auth/Passkey* endpoints. Login start/finish are
 * public (public client); register/list/delete require a JWT (http client).
 */

import { getPublicClient } from '@/api/client/public-client';
import { getHttpClient } from '@/api/client/http-client';
import type {
  PasskeyRegisterStartResponse,
  PasskeyRegisterFinishRequest,
  PasskeyRegisterFinishResponse,
  PasskeyLoginStartResponse,
  PasskeyLoginFinishRequest,
  PasskeyLoginFinishResponse,
  PasskeyListResponse,
  PasskeyDeleteRequest,
  PasskeyDeleteResponse,
} from '@/api/models/passkey.types';

/** POST /v1_0/UserPortal/Auth/PasskeyRegisterStart — authenticated */
export async function registerStart(): Promise<PasskeyRegisterStartResponse> {
  const client = getHttpClient();
  const response = await client.post<PasskeyRegisterStartResponse>('/Auth/PasskeyRegisterStart', {});
  return response.data;
}

/** POST /v1_0/UserPortal/Auth/PasskeyRegisterFinish — authenticated */
export async function registerFinish(
  req: PasskeyRegisterFinishRequest,
): Promise<PasskeyRegisterFinishResponse> {
  const client = getHttpClient();
  const response = await client.post<PasskeyRegisterFinishResponse>('/Auth/PasskeyRegisterFinish', req);
  return response.data;
}

/** POST /v1_0/UserPortal/Auth/PasskeyLoginStart — public */
export async function loginStart(): Promise<PasskeyLoginStartResponse> {
  const client = getPublicClient();
  const response = await client.post<PasskeyLoginStartResponse>('/PasskeyLoginStart', {});
  return response.data;
}

/** POST /v1_0/UserPortal/Auth/PasskeyLoginFinish — public, passwordless */
export async function loginFinish(
  req: PasskeyLoginFinishRequest,
): Promise<PasskeyLoginFinishResponse> {
  const client = getPublicClient();
  const response = await client.post<PasskeyLoginFinishResponse>('/PasskeyLoginFinish', req);
  return response.data;
}

/** GET /v1_0/UserPortal/Auth/PasskeyList — authenticated */
export async function list(): Promise<PasskeyListResponse> {
  const client = getHttpClient();
  const response = await client.get<PasskeyListResponse>('/Auth/PasskeyList');
  return response.data;
}

/** DELETE /v1_0/UserPortal/Auth/PasskeyDelete — authenticated */
export async function remove(req: PasskeyDeleteRequest): Promise<PasskeyDeleteResponse> {
  const client = getHttpClient();
  const response = await client.delete<PasskeyDeleteResponse>('/Auth/PasskeyDelete', { data: req });
  return response.data;
}
